import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Save, X, Trash2, AlertTriangle, Copy, CheckCircle2, UserMinus, UserX, GraduationCap, Link2, ArchiveRestore, ChevronDown, ChevronUp, Users, BookOpen, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { AddLearnerSheet } from '../components/AddLearnerSheet';
import { ClassroomTeachersTab } from '../components/ClassroomTeachersTab';
import { CoTeacherWelcomeModal } from '../components/CoTeacherWelcomeModal';
import { TransferClassroomModal } from '../components/TransferClassroomModal';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayNarrator } from '../../../mascot';
import { getLearnerClassroomFlags } from '../utils/studentUtils';
import { type Classroom, type TeacherAccount, type ClassroomValidationWarning, generateClassroomId, validateNewClassroom, findDuplicateClassroomName, isLearnerAccount, findClassroomByJoinCode, getClassroomRole, MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
import { StudentData } from './StudentDetailPanel';
interface ManagementPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  students: StudentData[];
}
export function ManagementPage({
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  students
}: ManagementPageProps) {
  const {
    accounts,
    addClassroom,
    updateClassroom,
    removeClassroom,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner,
    restoreArchivedLearner,
    joinClassroomAsCoTeacher,
    regenerateJoinCode
  } = useAppStore();
  const [isJoiningAsCoTeacher, setIsJoiningAsCoTeacher] = useState(false);
  const [coTeacherCode, setCoTeacherCode] = useState('');
  const [coTeacherJoinError, setCoTeacherJoinError] = useState<string | null>(null);
  const [coTeacherWelcomeFor, setCoTeacherWelcomeFor] = useState<Classroom | null>(null);
  const handleJoinAsCoTeacher = () => {
    setCoTeacherJoinError(null);
    const code = coTeacherCode.trim().toUpperCase();
    if (!code) {
      setCoTeacherJoinError('Vul eerst een code in.');
      return;
    }
    const target = findClassroomByJoinCode(accounts, code);
    if (!target) {
      setCoTeacherJoinError('Geen klas gevonden met deze code.');
      return;
    }
    const role = getClassroomRole(accounts, target.classroom.id, activeAccount.id);
    if (role === 'owner') {
      setCoTeacherJoinError('Je bent al de eigenaar van deze klas.');
      return;
    }
    if (role === 'co-teacher') {
      setCoTeacherJoinError('Je bent al co-docent in deze klas.');
      return;
    }
    const currentCoTeachers = target.classroom.coTeacherIds ?? [];
    if (currentCoTeachers.length >= MAX_CO_TEACHERS_PER_CLASSROOM) {
      setCoTeacherJoinError(`Deze klas heeft al ${MAX_CO_TEACHERS_PER_CLASSROOM} co-docenten. Vraag de eigenaar om iemand te verwijderen.`);
      return;
    }
    joinClassroomAsCoTeacher(activeAccount.id, target.classroom.id);
    setCoTeacherWelcomeFor(target.classroom);
    setIsJoiningAsCoTeacher(false);
    setCoTeacherCode('');
  };
  const [isEditingClass, setIsEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [editGroupLevel, setEditGroupLevel] = useState(6);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [deleteClassroomId, setDeleteClassroomId] = useState<string | null>(null);
  const [transferClassroomId, setTransferClassroomId] = useState<string | null>(null);
  const [regenerateCodeClassroomId, setRegenerateCodeClassroomId] = useState<string | null>(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [newClassroomId, setNewClassroomId] = useState<string | null>(null);
  const [pendingRemoveStudent, setPendingRemoveStudent] = useState<{
    studentId: string;
    studentName: string;
    classroomId: string;
    classroomName: string;
  } | null>(null);
  const [pendingUnlinkStudent, setPendingUnlinkStudent] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [activeTab, setActiveTab] = useState<'leerlingen' | 'klassen' | 'docenten' | 'archief'>('leerlingen');
  const handleSaveClass = (classroomId: string) => {
    if (editClassName.trim()) {
      const duplicates = findDuplicateClassroomName(accounts, editClassName.trim(), classroomId);
      if (duplicates.length > 0) {
        const dup = duplicates[0];
        const isSameTeacher = dup.classroom.teacherId === activeAccount.id;
        setClassWarnings([{
          type: 'duplicate_name',
          message: isSameTeacher ? `Je hebt al een klas met de naam "${dup.classroom.name}".` : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}.`,
          details: {
            classroomName: dup.classroom.name,
            classroomId: dup.classroom.id,
            teacherName: dup.teacherName,
            teacherId: dup.classroom.teacherId
          }
        }]);
        return;
      }
      updateClassroom(activeAccount.id, classroomId, {
        name: editClassName.trim(),
        groupLevel: editGroupLevel
      });
      setIsEditingClass(null);
      setClassWarnings([]);
    }
  };
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    addClassroom(activeAccount.id, newClassroom);
    setIsAddingClass(false);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
    setNewClassroomId(newClassroom.id);
    setTimeout(() => setNewClassroomId(null), 3000);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const classStudents = (activeClassroom ? students.filter(s => activeClassroom.learnerIds.includes(s.id)) : students).sort((a, b) => a.name.localeCompare(b.name));
  const classroomToDelete = deleteClassroomId ? classrooms.find(c => c.id === deleteClassroomId) : null;
  const archivedLinks = accounts.relationships.archivedLearnerLinks?.[activeAccount.id] || [];
  const recentArchivedLinks = archivedLinks.filter(link => Date.now() - link.archivedAt < 30 * 24 * 60 * 60 * 1000).sort((a, b) => b.archivedAt - a.archivedAt);
  return <PageLayout title="Klas Beheer" description="Beheer je klassen en leerlingen" data-id="element-9706" data-mp="s8yy7geyeto7">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-9707" data-mp="dk2fmpf9ty9c">
        {/* Segmented Tabs */}
        <div className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md p-1.5 rounded-xl border border-slate-200 flex items-center gap-1 overflow-x-auto custom-scrollbar shadow-sm" data-id="element-9708" data-mp="di85851mloji">
          <button onClick={() => setActiveTab('leerlingen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'leerlingen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-9709" data-mp="4lh2xawhddgh">
            <Users className="w-4 h-4" data-id="element-9710" data-mp="1ozhg436ov897" />
            Leerlingen
          </button>
          <button onClick={() => setActiveTab('klassen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'klassen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-9711" data-mp="1tyc5jnz5vv2a">
            <BookOpen className="w-4 h-4" data-id="element-9712" data-mp="3o6fyqd55hs64" />
            Klassen
          </button>
          <button onClick={() => setActiveTab('docenten')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'docenten' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-9713" data-mp="3jfpsyystbnaw">
            <UserCheck className="w-4 h-4" data-id="element-9714" data-mp="ayl7igtauy15" />
            Docenten
          </button>
          {recentArchivedLinks.length > 0 && <button onClick={() => setActiveTab('archief')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'archief' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-9715" data-mp="5ygrerwb05bg">
              <ArchiveRestore className="w-4 h-4" data-id="element-9716" data-mp="jakf3brp7uqf" />
              Archief ({recentArchivedLinks.length})
            </button>}
        </div>

        {/* Students Management Section */}
        {activeTab === 'leerlingen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-9717" data-mp="abqivmia56de">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-9718" data-mp="16zmsu8j7p3fn">
              <div data-id="element-9719" data-mp="122wdxoj58dvt">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9720" data-mp="122d4yxbh0ztx">
                  Leerlingen Beheer
                </h3>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-9721" data-mp="t9t6bwv6iv93">
                  {activeClassroom ? `${activeClassroom.name} (${classStudents.length} leerlingen)` : `Alle gekoppelde leerlingen (${classStudents.length})`}
                </p>
              </div>
              <Button onClick={() => setShowAddStudent(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9722" data-mp="33nqohzy3wncz">
                <Plus className="w-4 h-4" data-id="element-9723" data-mp="3hn8iquu68stq" />
                Leerling Toevoegen
              </Button>
            </div>

            <div className="p-4" data-id="element-9724" data-mp="3mla45mf2l48d">
              {classStudents.length === 0 ? <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-9725" data-mp="3rkczug95ty3o">
                  <div className="mb-3 flex justify-center" data-id="element-9726" data-mp="7s17vb0wdk5">
                    <DjayNarrator size="md" emotion="surprised" data-id="element-9727" data-mp="47u2w4grd6zf" />
                  </div>
                  <p className="text-slate-500 font-medium" data-id="element-9728" data-mp="976rour6wo4n">
                    {activeClassroom ? `Nog geen leerlingen in ${activeClassroom.name}.` : 'Je hebt nog geen leerlingen gekoppeld.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-9729" data-mp="2a9xqw4bczsg3">
                    {activeClassroom ? 'Voeg leerlingen toe via de knop hierboven.' : 'Selecteer een klas in de zijbalk of voeg leerlingen toe.'}
                  </p>
                </div> : <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-9730" data-mp="2f6eft0qduau8">
                  <div className="flex items-center justify-between p-4 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9731" data-mp="2oyaqdb9nntfc">
                    <div data-id="element-9732" data-mp="32wqxh3bfjly0">Leerling</div>
                    <div data-id="element-9733" data-mp="32vplmwmlavf8">Acties</div>
                  </div>
                  {classStudents.map((student, i) => {
              const initial = student.name.charAt(0).toUpperCase();
              const flags = getLearnerClassroomFlags(accounts, student.id, activeClassroom || null);
              const learnerAccount = accounts.accountsById[student.id];
              const learnerGrade = learnerAccount && isLearnerAccount(learnerAccount) ? learnerAccount.grade : undefined;
              return <div key={student.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-9734" data-mp="3afi1nafapw30" data-mpi={i}>
                        <div className="flex items-center gap-3 flex-wrap" data-id="element-9735" data-mp="2mkp54wp0ebve">
                          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-9736" data-mp="2ri73jt5eh8p5">
                            {initial}
                          </div>
                          <div className="font-bold text-sm text-slate-900" data-id="element-9737" data-mp="pqasslr242vf">
                            {student.name}
                          </div>
                          {flags.gradeMismatch && learnerGrade && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-md" title={`Werkt op groep-${learnerGrade}-niveau (deze klas is groep ${activeClassroom?.groupLevel}).`} data-id="element-9738" data-mp="2htesxg5yydgz">
                              <GraduationCap className="w-3 h-3" data-id="element-9739" data-mp="1y4xqzn3lniai" />
                              Gr {learnerGrade}
                            </span>}
                          {flags.gradeUnknown && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-md" title="Deze leerling heeft nog geen groep ingesteld." data-id="element-9740" data-mp="1p50wh4uj3p7j">
                              <GraduationCap className="w-3 h-3" data-id="element-9741" data-mp="152mtxseu8jd8" />
                              Geen groep
                            </span>}
                          {(flags.multiClassroom || flags.multiTeacher) && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-100 text-sky-700 text-xs font-bold rounded-md" title={flags.otherEnrollments.length > 0 ? `Ook in: ${flags.otherEnrollments.map(e => e.classroomName + ' (' + e.teacherName + ')').join(', ')}` : 'Gekoppeld aan meer dan een leerkracht'} data-id="element-9742" data-mp="1f12jhqy7vkkm">
                              <Link2 className="w-3 h-3" data-id="element-9743" data-mp="3lxgsmy35m10" />
                              {flags.otherEnrollments.length > 0 ? '+' + flags.otherEnrollments.length + (flags.otherEnrollments.length === 1 ? ' klas' : ' klassen') : 'Meerdere koppelingen'}
                            </span>}
                          {!activeClassroom && !flags.gradeMismatch && !flags.gradeUnknown && learnerGrade && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded" data-id="element-9744" data-mp="38s18ht1kkfmk">
                                Gr {learnerGrade}
                              </span>}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-9745" data-mp="12y00lcxkjv1g">
                          {activeClassroom ? <>
                              <button onClick={() => {
                      setPendingRemoveStudent({
                        studentId: student.id,
                        studentName: student.name,
                        classroomId: activeClassroom.id,
                        classroomName: activeClassroom.name
                      });
                    }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Verwijder uit klas" data-id="element-9746" data-mp="xy59p9dsn5ge">
                                <UserX className="w-4 h-4" data-id="element-9747" data-mp="1jhp77xw864mx" />
                                <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9748" data-mp="1jg4d29dtonpl">
                                  Verwijder uit klas
                                </span>
                              </button>
                              {getClassroomRole(accounts, activeClassroom.id, activeAccount.id) === 'owner' && <>
                                  <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-9749" data-mp="18j7rtyaj7gva"></div>
                                  <button onClick={() => {
                        setPendingUnlinkStudent({
                          studentId: student.id,
                          studentName: student.name
                        });
                      }} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Ontkoppel volledig van jouw account" data-id="element-9750" data-mp="266q0za83ukzt">
                                    <UserMinus className="w-4 h-4" data-id="element-9751" data-mp="2ri5q4ofuqons" />
                                  </button>
                                </>}
                            </> : <button onClick={() => {
                    setPendingUnlinkStudent({
                      studentId: student.id,
                      studentName: student.name
                    });
                  }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Ontkoppel volledig van jouw account" data-id="element-9752" data-mp="31e8v022391ef">
                              <UserMinus className="w-4 h-4" data-id="element-9753" data-mp="1puejtjcdl9t3" />
                              <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-9754" data-mp="1usg2w49sx94k">
                                Ontkoppel
                              </span>
                            </button>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>
          </Card>}

        {/* Classes Section */}
        {activeTab === 'klassen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-9755" data-mp="lfqvb4tiznxd">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-9756" data-mp="qek6urp954uw">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9757" data-mp="vg150fgcqd7s">
                Mijn Klassen
              </h3>
              <div className="flex items-center gap-2" data-id="element-9758" data-mp="zdyxps9bzhfx">
                <Button onClick={() => setIsAddingClass(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9759" data-mp="2x7e9x4ibkl3u">
                  <Plus className="w-4 h-4" data-id="element-9760" data-mp="2ovvpfgzplzk3" />
                  Nieuwe Klas
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-4" data-id="element-9761" data-mp="2fwqx4seqr2gd">
              {isAddingClass && <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl" data-id="element-9762" data-mp="2funxrhrp7wyz">
                  <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-9763" data-mp="3ckx59pavgcze">
                    Nieuwe klas toevoegen
                  </h4>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-9764" data-mp="33ltlsk7slvkd">
                    <div className="flex-1 w-full" data-id="element-9765" data-mp="2yni8u0m7b4xx">
                      <Input variant="admin" label="Klasnaam" value={newClassName} onChange={e => setNewClassName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddClassroom();
                  }
                }} placeholder="Bijv. Groep 7B" autoFocus data-id="element-9766" data-mp="2ymytmrnxqunl" />
                    </div>
                    <div className="w-full sm:w-32" data-id="element-9767" data-mp="1js8lm34q1avf">
                      <Select variant="admin" label="Niveau" value={newGroupLevel} onChange={e => setNewGroupLevel(parseInt(e.target.value))} options={[{
                  value: '6',
                  label: 'Groep 6'
                }, {
                  value: '7',
                  label: 'Groep 7'
                }, {
                  value: '8',
                  label: 'Groep 8'
                }]} data-id="element-9768" data-mp="15yhet2idk53e" />
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-9769" data-mp="hf794c0daarl">
                      <Button onClick={handleAddClassroom} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9770" data-mp="dazha5ym4ep5">
                        <Save className="w-4 h-4" data-id="element-9771" data-mp="1124zgrs0zj2m" />
                        Aanmaken
                      </Button>
                      <Button onClick={() => {
                  setIsAddingClass(false);
                  setClassWarnings([]);
                  setConfirmDuplicate(false);
                }} variant="secondary" data-id="element-9772" data-mp="w43chvrhazlv">
                        Annuleren
                      </Button>
                    </div>
                  </div>
                  {classWarnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-9773" data-mp="3998fqk5vpn0i">
                      {classWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.details.teacherId === activeAccount.id ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-id="element-9774" data-mp="3da0prfmkrvxu" data-mpi={i}>
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-9775" data-mp="2pk6cwy29a7dl" />
                          <div data-id="element-9776" data-mp="2uldjwuwkr2j7">
                            <p className="font-medium" data-id="element-9777" data-mp="2uihphgjxqvzi">{w.message}</p>
                            {w.details.teacherId !== activeAccount.id && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-1 text-xs font-bold underline hover:no-underline" data-id="element-9778" data-mp="9w6cmanzhhdq">
                                  Ja, toch aanmaken
                                </button>}
                          </div>
                        </div>)}
                    </div>}
                </div>}

              {classrooms.length === 0 && !isAddingClass ? <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-9779" data-mp="3sg6dxgvw7cm1">
                  <div className="mb-4 flex justify-center" data-id="element-9780" data-mp="3jdf39jauckvg">
                    <DjayNarrator size="md" emotion="thinking" data-id="element-9781" data-mp="3jezxt8x5o3tb" />
                  </div>
                  <p className="text-slate-500 mb-4 font-medium" data-id="element-9782" data-mp="1lmchz9pl91wq">
                    Je hebt nog geen klassen aangemaakt.
                  </p>
                  <Button onClick={() => setIsAddingClass(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-9783" data-mp="3b4rfomsflcm0">
                    Maak je eerste klas aan
                  </Button>
                </div> : <div className="grid grid-cols-1 gap-3" data-id="element-9784" data-mp="3667q8cempiow">
                  {classrooms.map((classroom: Classroom) => <motion.div key={classroom.id} initial={newClassroomId === classroom.id ? {
              backgroundColor: '#f0fdf4',
              borderColor: '#86efac'
            } : {}} animate={{
              backgroundColor: '#ffffff',
              borderColor: '#e2e8f0'
            }} transition={{
              duration: 2,
              delay: 1
            }} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm" data-id="element-9785" data-mp="3ttfxv0zj53u3">
                      {isEditingClass === classroom.id ? <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full" data-id="element-9786" data-mp="1n81qn5y7coqw">
                          <div className="flex-1 w-full" data-id="element-9787" data-mp="1i9i4p5jlzcyj">
                            <Input variant="admin" value={editClassName} onChange={e => setEditClassName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveClass(classroom.id);
                    }
                  }} placeholder="Klasnaam" autoFocus data-id="element-9788" data-mp="1wxbl2sgiqji6" />
                          </div>
                          <div className="w-full sm:w-32" data-id="element-9789" data-mp="nydbhwcmq2nv">
                            <Select variant="admin" value={editGroupLevel} onChange={e => setEditGroupLevel(parseInt(e.target.value))} options={[{
                    value: '6',
                    label: 'Groep 6'
                  }, {
                    value: '7',
                    label: 'Groep 7'
                  }, {
                    value: '8',
                    label: 'Groep 8'
                  }]} data-id="element-9790" data-mp="swesmbqhvrwp" />
                          </div>
                          <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-0" data-id="element-9791" data-mp="2w45r3xsc3eyv">
                            <Button onClick={() => handleSaveClass(classroom.id)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9792" data-mp="2r6ne9wb74fll">
                              <Save className="w-4 h-4" data-id="element-9793" data-mp="3f0oqhvdc5oi8" />
                              Opslaan
                            </Button>
                            <Button onClick={() => {
                    setIsEditingClass(null);
                    setClassWarnings([]);
                  }} variant="secondary" size="sm" data-id="element-9794" data-mp="bderupoq5ubs">
                              <X className="w-4 h-4" data-id="element-9795" data-mp="13ebm7aj8hz45" />
                            </Button>
                          </div>
                        </div> : <>
                          <div className="flex items-center gap-4 mb-3 sm:mb-0" data-id="element-9796" data-mp="1cc42agws5asl">
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-lg border border-indigo-100" data-id="element-9797" data-mp="1r9ek0800ncwr">
                              {classroom.groupLevel}
                            </div>
                            <div data-id="element-9798" data-mp="1w1o4h8id3krn">
                              <h4 className="font-bold text-slate-900 text-base" data-id="element-9799" data-mp="1w2phvjmuojcj">
                                {classroom.name}
                              </h4>
                              <div className="flex items-center gap-3 text-sm text-slate-500 font-medium mt-0.5" data-id="element-9800" data-mp="252mec7fhemi5">
                                <span data-id="element-9801" data-mp="2onmlqeggx044">
                                  {classroom.learnerIds.length} leerlingen
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-9802" data-mp="2tkms6qh8c4vr" />
                                <span data-id="element-9803" data-mp="2epghhq76vhw3">
                                  {(classroom.coTeacherIds?.length || 0) + 1}{' '}
                                  docenten
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-9804" data-mp="25vbmcul0vkar" />
                                <div className="flex items-center gap-1.5" data-id="element-9805" data-mp="2apfuvi9xs0dy">
                                  <span data-id="element-9806" data-mp="1w0a149tvb2dg">Code:</span>
                                  <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-9807" data-mp="20ybfwik9gfj4">
                                    {classroom.joinCode}
                                  </span>
                                  {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' && <>
                                      <button onClick={() => handleCopyCode(classroom.joinCode)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Kopieer code" data-id="element-9808" data-mp="3jwgegrp1hy2z">
                                        {copiedCode === classroom.joinCode ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-9809" data-mp="2h9nbguulrzm" /> : <Copy className="w-3.5 h-3.5" data-id="element-9810" data-mp="2r9n89tsvs6ll" />}
                                      </button>
                                      <button onClick={() => setRegenerateCodeClassroomId(classroom.id)} className="text-xs text-indigo-600 hover:text-indigo-700 font-bold ml-1" data-id="element-9811" data-mp="12ppug6uzic8v">
                                        Nieuwe code
                                      </button>
                                    </>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-9812" data-mp="2cvyvtaaqle">
                            {newClassroomId === classroom.id && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mr-2" data-id="element-9813" data-mp="51pkrm3pu7qm">
                                Nieuw!
                              </span>}
                            {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' ? <>
                                <Button onClick={() => {
                      if (isEditingClass !== classroom.id) {
                        setClassWarnings([]);
                      }
                      setIsEditingClass(classroom.id);
                      setEditClassName(classroom.name);
                      setEditGroupLevel(classroom.groupLevel);
                    }} variant="ghost" size="sm" data-id="element-9814" data-mp="2n6fkb596t97y">
                                  <Edit2 className="w-4 h-4 mr-2" data-id="element-9815" data-mp="ew7qhovzax6q" /> Bewerk
                                </Button>
                                <Button onClick={() => setTransferClassroomId(classroom.id)} variant="ghost" size="sm" data-id="element-9816" data-mp="4zuv8mkscb9v">
                                  Draag over...
                                </Button>
                                <Button onClick={() => setDeleteClassroomId(classroom.id)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-9817" data-mp="oodigym8delx">
                                  <Trash2 className="w-4 h-4" data-id="element-9818" data-mp="1jvwa5jsqrixd" />
                                </Button>
                              </> : <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-700 rounded" title="Je bent co-docent in deze klas — alleen de eigenaar kan klasinstellingen wijzigen" data-id="element-9819" data-mp="1a6upvhf5yfdl">
                                Co-docent
                              </span>}
                          </div>
                        </>}
                    </motion.div>)}
                </div>}
            </div>
          </Card>}

        {/* Teachers Management Section */}
        {activeTab === 'docenten' && <div className="space-y-6" data-id="element-9820" data-mp="2gohyg15xs399">
            {activeClassroom ? <ClassroomTeachersTab classroom={activeClassroom} activeAccount={activeAccount} data-id="element-9821" data-mp="2p4qpbruhbd4n" /> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-9822" data-mp="2t3psp80kb1tz">
                <div className="mb-4 flex justify-center" data-id="element-9823" data-mp="32vm0jasga6be">
                  <DjayNarrator size="md" emotion="thinking" data-id="element-9824" data-mp="32v3whrzjqnsx" />
                </div>
                <p className="text-slate-500 font-medium" data-id="element-9825" data-mp="266edfyc6np5d">
                  Selecteer eerst een klas in de zijbalk om co-docenten te
                  beheren.
                </p>
              </div>}

            {/* Join as Co-Teacher Section */}
            <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-9826" data-mp="2k547f53ph8pi">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-9827" data-mp="2k2rksen06a64">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-9828" data-mp="3qky9qans2edp">
                  Toetreden tot een klas
                </h3>
              </div>
              <div className="p-4" data-id="element-9829" data-mp="kjz3yvuykmrw">
                {!isJoiningAsCoTeacher ? <Button onClick={() => {
              setIsJoiningAsCoTeacher(true);
              setCoTeacherJoinError(null);
            }} variant="secondary" className="w-full sm:w-auto" data-id="element-9830" data-mp="gi59x6f9qh0p">
                    Ik heb een co-docent-code
                  </Button> : <div className="p-4 bg-amber-50/60 border border-amber-100 rounded-xl" data-id="element-9831" data-mp="z8d9jejrepc5">
                    <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-9832" data-mp="1d7cvufjsus55">
                      Ik heb een co-docent-code
                    </h4>
                    <p className="text-xs text-slate-600 mb-3" data-id="element-9833" data-mp="1891lm3plefme">
                      Vraag de klas-eigenaar om de klascode en voer hem hier in.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-9834" data-mp="3a0ojmp7wjasd">
                      <div className="flex-1 w-full" data-id="element-9835" data-mp="1phkp1ici6727">
                        <Input variant="admin" label="Klascode" value={coTeacherCode} onChange={e => setCoTeacherCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleJoinAsCoTeacher();
                    }
                  }} placeholder="Bijv. ABC123" maxLength={8} autoFocus data-id="element-9836" data-mp="1pk6vtfrkkijd" />
                        {coTeacherJoinError && <p className="mt-2 text-xs text-rose-600" data-id="element-9837" data-mp="26srbmzgwm9f5">
                            {coTeacherJoinError}
                          </p>}
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-9838" data-mp="1wvmn4szl61v6">
                        <Button onClick={handleJoinAsCoTeacher} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9839" data-mp="21toee17617hq">
                          Word co-docent
                        </Button>
                        <Button onClick={() => {
                    setIsJoiningAsCoTeacher(false);
                    setCoTeacherCode('');
                    setCoTeacherJoinError(null);
                  }} variant="secondary" data-id="element-9840" data-mp="1e6ga9mlh44ha">
                          <X className="w-4 h-4" data-id="element-9841" data-mp="qgyojvwwc5rx" />
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>}

        {/* Archive Section */}
        {activeTab === 'archief' && recentArchivedLinks.length > 0 && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-9842" data-mp="uha4wwi87jxl">
            <div className="p-4 border-b border-slate-100 bg-slate-50" data-id="element-9843" data-mp="19f2k1njv26b9">
              <div className="flex items-center gap-2" data-id="element-9844" data-mp="14hj4mkbffnhq">
                <ArchiveRestore className="w-5 h-5 text-slate-500" data-id="element-9845" data-mp="14grazf1e3zu3" />
                <h3 className="text-sm font-bold text-slate-900" data-id="element-9846" data-mp="1i7cw7aebglv5">
                  Onlangs ontkoppeld ({recentArchivedLinks.length})
                </h3>
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-9847" data-mp="1s62bqwiqpu88">
                Na 30 dagen worden ontkoppelingen automatisch definitief.
              </p>
            </div>

            <div className="divide-y divide-slate-100 bg-white" data-id="element-9848" data-mp="1n5o25a1hqc83">
              {recentArchivedLinks.map(link => {
            const learner = accounts.accountsById[link.learnerId];
            if (!learner) return null;
            const daysAgo = Math.floor((Date.now() - link.archivedAt) / (1000 * 60 * 60 * 24));
            const timeText = daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : `${daysAgo} dagen geleden`;
            let contextText = 'uit account';
            if (link.reason === 'unlink' && link.fromClassroomIds.length > 0) {
              const classNames = link.fromClassroomIds.map(id => classrooms.find(c => c.id === id)?.name).filter(Boolean);
              if (classNames.length > 0) {
                contextText = `uit ${classNames.join(', ')}`;
              }
            } else if (link.reason === 'classroom_delete') {
              contextText = 'door klasverwijdering';
            }
            return <div key={link.learnerId} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-9849" data-mp="321mk7g5s8nc7">
                    <div data-id="element-9850" data-mp="3urbkfrao4jcr">
                      <div className="font-bold text-sm text-slate-900" data-id="element-9851" data-mp="3uqjnod5c4fkw">
                        {learner.displayName}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-9852" data-mp="3ksvv9tv7bk5w">
                        {timeText} · {contextText}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-9853" data-mp="3gtd6gn09eoxs">
                      <Button onClick={() => {
                  restoreArchivedLearner(activeAccount.id, link.learnerId, {
                    toClassroomId: link.fromClassroomIds[0]
                  });
                  toast.success(`${learner.displayName} is hersteld.`);
                }} variant="secondary" size="sm" className="text-xs font-bold" data-id="element-9854" data-mp="29es38t86c63h">
                        Herstel
                      </Button>
                    </div>
                  </div>;
          })}
            </div>
          </Card>}
      </div>

      <AddLearnerSheet open={showAddStudent} onClose={() => setShowAddStudent(false)} activeAccount={activeAccount} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onComplete={() => setShowAddStudent(false)} data-id="element-9855" data-mp="6syvihbvipps" />

      <CoTeacherWelcomeModal open={!!coTeacherWelcomeFor} classroom={coTeacherWelcomeFor} onClose={() => setCoTeacherWelcomeFor(null)} data-id="element-9856" data-mp="2vmmczf1424ga" />

      <TransferClassroomModal open={!!transferClassroomId} classroom={classrooms.find(c => c.id === transferClassroomId) || null} activeAccount={activeAccount} onClose={() => setTransferClassroomId(null)} data-id="element-9857" data-mp="2j38l5q84z0yd" />

      <ConfirmDialog open={!!regenerateCodeClassroomId} title="Nieuwe code genereren" message="De oude code stopt direct met werken. Leerlingen die hem nog niet gebruikt hebben moeten de nieuwe krijgen." confirmLabel="Ja, genereer nieuwe code" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (regenerateCodeClassroomId) {
        regenerateJoinCode(activeAccount.id, regenerateCodeClassroomId);
        setRegenerateCodeClassroomId(null);
      }
    }} onCancel={() => setRegenerateCodeClassroomId(null)} data-id="element-9858" data-mp="2syk12bva6e7p" />

      <ConfirmDialog open={!!deleteClassroomId} title="Klas verwijderen" message={classroomToDelete ? `Weet je zeker dat je "${classroomToDelete.name}" wilt verwijderen?\n\n• ${classroomToDelete.learnerIds.length} leerlingen blijven gekoppeld aan je account.\n• ${classroomToDelete.coTeacherIds?.length || 0} co-docenten verliezen direct toegang.\n• Koppelcode ${classroomToDelete.joinCode} vervalt direct.\n\nJe kunt dit niet ongedaan maken.` : ''} confirmLabel="Ja, verwijder klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteClassroomId) {
        removeClassroom(activeAccount.id, deleteClassroomId);
        setDeleteClassroomId(null);
      }
    }} onCancel={() => setDeleteClassroomId(null)} data-id="element-9859" data-mp="279llqfanvx58" />

      <ConfirmDialog open={!!pendingRemoveStudent} title="Leerling verwijderen uit klas" message={pendingRemoveStudent ? `Weet je zeker dat je ${pendingRemoveStudent.studentName} uit ${pendingRemoveStudent.classroomName} wilt verwijderen? ${pendingRemoveStudent.studentName} blijft wel gekoppeld aan jouw account.` : ''} confirmLabel="Ja, verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingRemoveStudent) {
        removeLearnerFromClassroom(activeAccount.id, pendingRemoveStudent.classroomId, pendingRemoveStudent.studentId);
        setPendingRemoveStudent(null);
      }
    }} onCancel={() => setPendingRemoveStudent(null)} data-id="element-9860" data-mp="38q2zmvo7rnoq" />

      <ConfirmDialog open={!!pendingUnlinkStudent} title="Koppeling verbreken" message={pendingUnlinkStudent ? `${pendingUnlinkStudent.studentName} wordt verwijderd uit al je klassen en je verliest toegang tot de voortgang. Het leerlingaccount blijft bestaan.` : ''} confirmLabel="Ja, ontkoppel" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingUnlinkStudent) {
        const studentId = pendingUnlinkStudent.studentId;
        const studentName = pendingUnlinkStudent.studentName;
        unlinkTeacherFromLearner(activeAccount.id, studentId);
        setPendingUnlinkStudent(null);
        toast.success(`${studentName} is ontkoppeld`, {
          action: {
            label: 'Ongedaan maken',
            onClick: () => {
              restoreArchivedLearner(activeAccount.id, studentId);
              toast.success(`${studentName} is weer gekoppeld.`);
            }
          },
          duration: 5000
        });
      }
    }} onCancel={() => setPendingUnlinkStudent(null)} data-id="element-9861" data-mp="4f3hk7essmwg" />
    </PageLayout>;
}